import { useQuery } from '@tanstack/react-query';
import { createContext, PropsWithChildren, useContext, useEffect, useMemo, useState } from 'react';
import {
  Collections,
  TypedPocketBase,
  UsersRecord,
  UsersResponse,
} from '../../types/pocketbase-types';
import { UserItem } from '@/backend/store/store';
import { authenticatedPbBrowserFromCookie } from '@/utils/pb-browser';
import { ClientStore } from '@/backend/store/client-store';
import { useRouter } from 'next/router';

type AuthContextData = {
  user: UserItem;
  pb:
    | { store: ClientStore; pb: TypedPocketBase; index: number }
    | { store: undefined; pb: undefined; index: number };
  isPending: boolean;
};

export const AuthContext = createContext<AuthContextData>({
  user: {
    avatar: '',
    collectionName: Collections.Users,
    collectionId: '',
    created: '',
    email: '',
    emailVisibility: false,
    id: '',
    name: '',
    parent_of: [],
    updated: '',
    username: '',
    verified: false,
  },
  pb: { store: undefined, pb: undefined, index: 0 },
  isPending: false,
});

export const AuthProvider = ({
  children,
}: PropsWithChildren<{
  authUser?: UsersResponse<UsersRecord>;
}>) => {
  const [storeIndex, setStoreIndex] = useState<{ i: number; store: ClientStore | undefined }>({
    i: 0,
    store: undefined,
  });
  const router = useRouter();
  const pb = useQuery({
    queryKey: ['pb-browser-auth'],
    queryFn: async () => {
      try {
        return await authenticatedPbBrowserFromCookie();
      } catch (e: unknown) {
        if ((e as any)?.message?.includes('Unauthorized')) {
          console.log('router redirecting to login', router);
          await router.push({ pathname: '/login', query: { redirect: window.location.pathname } });
          return { pb: {} as TypedPocketBase, user: {} as UsersResponse<UsersRecord> };
        }
        throw e;
      }
    },
  });
  useEffect(() => {
    if (!pb.data?.pb) {
      setStoreIndex((p) => ({ i: p.i + 1, store: undefined }));
      return;
    }
    setStoreIndex((p) => ({ i: p.i + 1, store: new ClientStore(pb.data.pb) }));
  }, [pb.data?.pb]);
  const pbStore = useMemo(() => {
    if (!pb.data?.pb || !storeIndex.store) {
      return { store: undefined, pb: undefined, index: storeIndex?.i ?? 0 };
    }
    return { pb: pb.data.pb, store: storeIndex.store, index: storeIndex.i };
  }, [pb.data?.pb, storeIndex.store, storeIndex.i]);
  return (
    <AuthContext.Provider
      value={{ user: pb.data?.user ?? ({} as UserItem), pb: pbStore, isPending: pb.isPending }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuthUser = () => {
  return useContext(AuthContext);
};
